import { useCallback, useRef } from "react";
import { useDebouncedFn } from "@jsq/ds/core";
import { useValidateDataPackageNameLazyQuery } from "~/graphql/supergraphHooks";

export const NAME_NOT_UNIQUE_ERROR = "Name already taken. Enter a unique name.";
const VALIDATION_DELAY_MS = 300;

export type DataPackageNameValidator = (name: string) => Promise<string | undefined>;

type UseDataPackageNameValidatorArgs = {
  arenaId: number | null;
  /** Pass the package's own id on rename flows so its current name isn't flagged against itself. */
  dataPackageId?: number;
};

type PendingResolver = (error: string | undefined) => void;
type PendingValidation = { name: string; resolve: PendingResolver };

/**
 * Async validator for Yup `.test(...)`. Debounced and superseded via a
 * request-id so only the latest call's response can set field state. Resolves
 * `undefined` on network errors — server-side mutations are the source of truth.
 */
export const useDataPackageNameValidator = ({
  arenaId,
  dataPackageId,
}: UseDataPackageNameValidatorArgs): DataPackageNameValidator => {
  const [validate] = useValidateDataPackageNameLazyQuery({
    fetchPolicy: "no-cache",
  });
  const pendingRef = useRef<PendingValidation | null>(null);
  const latestRequestIdRef = useRef(0);

  const runValidate = useDebouncedFn(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;

    const requestId = latestRequestIdRef.current;
    const { name, resolve } = pending;

    try {
      const { data } = await validate({
        variables: { arenaId, dataPackageId, name },
      });
      if (requestId !== latestRequestIdRef.current) {
        resolve(undefined);
        return;
      }
      const result = data?.finpubDataPackageValidate;
      resolve(!result || result.valid ? undefined : NAME_NOT_UNIQUE_ERROR);
    } catch {
      resolve(undefined);
    }
  }, VALIDATION_DELAY_MS);

  return useCallback(
    (name) => {
      const trimmed = name.trim();
      if (!trimmed) return Promise.resolve(undefined);

      pendingRef.current?.resolve(undefined);
      latestRequestIdRef.current += 1;

      return new Promise<string | undefined>((resolve) => {
        pendingRef.current = { name: trimmed, resolve };
        runValidate();
      });
    },
    [runValidate],
  );
};
